import { useMemo, useState } from "react";

export type DashboardDriverCard = {
  driverId: string;
  name: string;
  vehicleLabel: string;
  status: "in_transit" | "available" | "offline";
  orderCount: number;
  phoneDisplay: string;
};

type Filter = "all" | "in_transit" | "available" | "offline";

function countBy(drivers: DashboardDriverCard[], f: Filter): number {
  if (f === "all") return drivers.length;
  return drivers.filter((d) => d.status === f).length;
}

export function ActiveTransportersSection({
  drivers,
  globalSearch,
  selectedDriverId,
  onSelectDriverId,
}: {
  drivers: DashboardDriverCard[];
  /** From workspace header search (`/workspace/dashboard?q=`). */
  globalSearch: string;
  selectedDriverId: string | null;
  onSelectDriverId: (id: string | null) => void;
}) {
  const [filter, setFilter] = useState<Filter>("all");

  const filtered = useMemo(() => {
    let list = drivers;
    if (filter !== "all") list = list.filter((d) => d.status === filter);
    const qv = globalSearch.trim().toLowerCase();
    if (qv) {
      list = list.filter((d) => {
        const hay = [d.name, d.vehicleLabel, d.driverId, d.phoneDisplay, d.status].join(" ").toLowerCase();
        return hay.includes(qv);
      });
    }
    return list;
  }, [drivers, filter, globalSearch]);

  const tabs: { key: Filter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "in_transit", label: "In Progress" },
    { key: "available", label: "Available" },
    { key: "offline", label: "Offline" },
  ];

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <h3 className="text-base font-bold text-td-ink">Active Transporters</h3>
        <div className="flex flex-wrap gap-3">
          {tabs.map((t) => {
            const n = countBy(drivers, t.key);
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setFilter(t.key)}
                className={`cursor-pointer border-b-2 px-1 py-0.5 text-sm transition-colors ${
                  active
                    ? "border-td-ink font-semibold text-td-ink"
                    : "border-transparent text-td-muted hover:text-td-ink"
                }`}
              >
                <span>{t.label}</span>
                <span className="ml-1.5 rounded-md bg-[#F0F0F0] px-1.5 py-0.5 text-xs font-semibold text-[#555555]">
                  {n}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-4">
        {filtered.length === 0 ? (
          <p className="rounded-2xl border border-td-card-border bg-white px-4 py-8 text-center text-sm text-td-muted">
            {globalSearch.trim()
              ? "No transporters match your search."
              : "No transporters in this view."}
          </p>
        ) : null}
        {filtered.map((d) => {
          const isSelected = selectedDriverId === d.driverId;
          return (
            <div
              key={d.driverId}
              role="button"
              tabIndex={0}
              onClick={() => onSelectDriverId(isSelected ? null : d.driverId)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectDriverId(isSelected ? null : d.driverId);
                }
              }}
              className={`cursor-pointer rounded-2xl border p-5 shadow-td-card-soft transition-all duration-150 ${
                isSelected ? "border-[#111111] bg-[#FAFAFA]" : "border-td-card-border bg-white hover:border-[#CCCCCC]"
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#E8E8E8] text-lg font-bold text-[#555555]">
                  {d.name.slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-bold text-td-ink">{d.name}</p>
                  <p className="text-sm text-td-muted">{d.vehicleLabel}</p>
                </div>
                <button
                  type="button"
                  className="ml-auto shrink-0 rounded-xl bg-[#111111] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#333333]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectDriverId(isSelected ? null : d.driverId);
                  }}
                >
                  {isSelected ? (
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-td-green" />
                      Tracking
                    </span>
                  ) : (
                    "Track"
                  )}
                </button>
              </div>
              <div className="mt-3.5 flex flex-wrap items-center gap-5">
                <StatusPill status={d.status} />
                <span className="flex items-center gap-1.5 text-sm text-[#555555]">
                  <OrderIcon className="h-4 w-4 text-td-muted-light" />
                  {d.orderCount} orders in progress
                </span>
                <span className="flex items-center gap-1.5 text-sm text-[#555555]">
                  <PhoneIcon className="h-3.5 w-3.5 text-td-muted-light" />
                  {d.phoneDisplay}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function StatusPill({ status }: { status: DashboardDriverCard["status"] }) {
  if (status === "in_transit") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#111111] px-3 py-1.5 text-xs font-medium text-white">
        <TruckIcon className="h-3.5 w-3.5" />
        In Transit
      </span>
    );
  }
  if (status === "available") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#F0F0F0] px-3 py-1.5 text-xs font-medium text-[#555555]">
        Available
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#E8E8E8] px-3 py-1.5 text-xs font-medium text-[#555555]">
      Offline
    </span>
  );
}

function OrderIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function TruckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  );
}
